import type {InternalAxiosRequestConfig, AxiosInstance, AxiosError} from 'axios'
import {OKTA_TOKEN_STORAGE_KEY} from '@/utils/constants/globalConstants'
import {oktaAccessToken} from '@/config/oktaConfig'
import {clearOktaTokens} from '@/utils/authUtils'

export const APP_ROUTES = {
  LOGIN: '/login',
  FORBIDDEN: '/forbidden',
  HOME: '/',
}

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'
const hasStorage = typeof localStorage !== 'undefined'

// Vitest sets import.meta.env.VITEST = true
// MODE will typically be "test" while running unit tests
const env = (typeof import.meta !== 'undefined' ? (import.meta as any).env : {}) || {}
const isTest = !!env?.VITEST || env?.MODE === 'test'

let redirectTimer: ReturnType<typeof setTimeout> | null = null

function getAccessTokenFromStorage(): string | undefined {
  if (!hasStorage) return undefined
  const tokenStorage = localStorage.getItem(OKTA_TOKEN_STORAGE_KEY)
  if (!tokenStorage) return undefined
  try {
    const authStateStorage = JSON.parse(tokenStorage)
    return authStateStorage?.accessToken?.accessToken
  } catch (e) {
    console.error('Error parsing token from storage:', e)
    return undefined
  }
}

function getDevFallbackToken(): string | undefined {
  if (!hasStorage) return undefined
  return localStorage.getItem('accessToken') || undefined
}

function scheduleLoginRedirect(delay = 100) {
  if (!isBrowser || isTest) return
  if (redirectTimer) clearTimeout(redirectTimer)
  redirectTimer = setTimeout(() => {
    if (!isBrowser) return
    // Check if we're already on login page to prevent redirect loops
    const currentPath = window.location.pathname
    if (currentPath !== APP_ROUTES.LOGIN) {
      window.location.href = APP_ROUTES.LOGIN
    }
  }, delay)
}

/**
 * Authentication interceptor for adding auth token to requests
 */
export const authInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  try {
    let accessToken = oktaAccessToken()
    if (!accessToken) {
      accessToken = getAccessTokenFromStorage()
    }
    if (!accessToken) {
      accessToken = getDevFallbackToken()
    }

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    } else {
      console.error('No access token available for API request')
      if (isBrowser) {
        const currentPath = window.location.pathname
        if (currentPath !== APP_ROUTES.LOGIN && !currentPath.includes('/login/callback')) {
          console.warn('Redirecting to login due to missing authentication token')
          scheduleLoginRedirect(100)
        }
      }
    }
    return config
  } catch (error) {
    console.error('Error in auth interceptor:', error)
    return config // do not break the request chain
  }
}

// Helper function to handle enhanced 401 error handling
function handleEnhanced401Error() {
  if (typeof globalThis !== 'undefined' && (globalThis as any).handle401Error) {
    ;(globalThis as any).handle401Error()
    return true
  }
  return false
}

// Helper function to handle fallback authentication error behavior
function handleFallbackAuthError(status: number) {
  if (hasStorage) {
    clearOktaTokens()
  }

  if (isBrowser) {
    const currentPath = window.location.pathname
    if (currentPath !== APP_ROUTES.LOGIN && !currentPath.includes('/login/callback')) {
      console.warn(`Redirecting to login due to ${status} error`)
      scheduleLoginRedirect(0)
    }
  }
}

// Helper function to handle authentication errors
function handleAuthenticationError(status: number, responseData: any) {
  console.error(`Authentication error: ${status}`, responseData)

  const isEnhancedHandled = handleEnhanced401Error()
  if (!isEnhancedHandled) {
    handleFallbackAuthError(status)
  }
}

// Helper function to process response errors
function processResponseError(error: AxiosError) {
  if (error?.response) {
    const status = error.response.status

    if (status === 401) {
      handleAuthenticationError(status, error.response.data)
    }
  }
  return Promise.reject(error)
}

/**
 * Attaches the auth interceptor to an Axios instance
 */
export const attachAuthInterceptor = (axiosInstance: AxiosInstance): AxiosInstance => {
  // Request interceptor
  axiosInstance.interceptors.request.use(authInterceptor, (error: AxiosError) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  })

  // Response interceptor (auth errors)
  axiosInstance.interceptors.response.use(response => response, processResponseError)

  return axiosInstance
}

// Optional: expose a helper to clear timers in tests
export const __clearAuthRedirectTimerForTests = () => {
  if (redirectTimer) {
    clearTimeout(redirectTimer)
    redirectTimer = null
  }
}
